import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Psychology as PsychologyIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  FlashOn as BulkIcon,
  WhatsApp as WhatsAppIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { ClientService } from '../services/clientService';
import {
  Client,
  Workplace,
  PsagotTeamMember,
  Sector,
  Company,
  ClientInterest,
  CreateClientRequest,
  UpdateClientRequest
} from '../types/client';
import { countryCodes } from '../utils/countryCodes';
import RelevantDocuments from '../components/RelevantDocuments';
import InterestNotifyControls from '../components/InterestNotifyControls';
import ActivityTimeline from '../components/ActivityTimeline';
import CRMDashboard from '../components/CRMDashboard';
import BulkActions from '../components/BulkActions';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

const ClientManagement: React.FC = () => {
  // State management
  const [clients, setClients] = useState<Client[]>([]);
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [psagotTeam, setPsagotTeam] = useState<PsagotTeamMember[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientInterests, setClientInterests] = useState<ClientInterest[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showRelevantDocuments, setShowRelevantDocuments] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Dialog states
  const [addClientDialog, setAddClientDialog] = useState(false);
  const [editClientDialog, setEditClientDialog] = useState(false);
  const [addWorkplaceDialog, setAddWorkplaceDialog] = useState(false);

  // Form states
  const [newClient, setNewClient] = useState<CreateClientRequest>({
    first_name: '',
    last_name: '',
    email: '',
    phone_country_code: '+972',
    phone_number: '',
    workplace_id: undefined,
    psagot_team_id: undefined
  });

  const [editClient, setEditClient] = useState<UpdateClientRequest>({});
  const [newWorkplace, setNewWorkplace] = useState({ name: '', address: '', city: '', country: 'Israel' });

  // Interest management
  const [selectedSector, setSelectedSector] = useState<number | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [thematicQuery, setThematicQuery] = useState('');

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [
        clientsData,
        workplacesData,
        psagotTeamData,
        sectorsData,
        companiesData
      ] = await Promise.all([
        ClientService.getClients(),
        ClientService.getWorkplaces(),
        ClientService.getPsagotTeam(),
        ClientService.getSectors(),
        ClientService.getCompanies()
      ]);

      setClients(clientsData);
      setWorkplaces(workplacesData);
      setPsagotTeam(psagotTeamData);
      setSectors(sectorsData);
      setCompanies(companiesData);
    } catch (err) {
      setError('Failed to load data: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = async (client: Client) => {
    setSelectedClient(client);
    try {
      const interests = await ClientService.getClientInterests(client.id);
      setClientInterests(interests);
    } catch (err) {
      setError('Failed to load client interests: ' + (err as Error).message);
    }
  };

  const reloadInterests = async () => {
    if (!selectedClient) return;
    try {
      const interests = await ClientService.getClientInterests(selectedClient.id);
      setClientInterests(interests);
    } catch (err) {
      console.error('Failed to reload interests:', err);
    }
  };

  const handleAddClient = async () => {
    try {
      if (!newClient.first_name || !newClient.last_name || !newClient.email) {
        setError('First name, last name, and email are required');
        return;
      }

      const createdClient = await ClientService.createClient(newClient);
      setClients([...clients, createdClient]);
      setAddClientDialog(false);
      setNewClient({
        first_name: '',
        last_name: '',
        email: '',
        phone_country_code: '+972',
        phone_number: '',
        workplace_id: undefined,
        psagot_team_id: undefined
      });
      setSuccess('Client added successfully!');
    } catch (err) {
      setError('Failed to add client: ' + (err as Error).message);
    }
  };

  const handleUpdateClient = async () => {
    if (!selectedClient) return;

    try {
      const updatedClient = await ClientService.updateClient(selectedClient.id, editClient);
      setClients(clients.map(c => c.id === selectedClient.id ? updatedClient : c));
      setSelectedClient(updatedClient);
      setEditClientDialog(false);
      setEditClient({});
      setSuccess('Client updated successfully!');
    } catch (err) {
      setError('Failed to update client: ' + (err as Error).message);
    }
  };

  const handleAddWorkplace = async () => {
    try {
      if (!newWorkplace.name) {
        setError('Workplace name is required');
        return;
      }

      const createdWorkplace = await ClientService.createWorkplace(newWorkplace);
      setWorkplaces([...workplaces, createdWorkplace]);
      setAddWorkplaceDialog(false);
      setNewWorkplace({ name: '', address: '', city: '', country: 'Israel' });
      setSuccess('Workplace added successfully!');
    } catch (err) {
      setError('Failed to add workplace: ' + (err as Error).message);
    }
  };

  const handleAddInterest = async () => {
    if (!selectedClient) return;

    try {
      if (!selectedSector && !selectedCompany && !thematicQuery.trim()) {
        setError('Please select a sector, company, or enter a thematic interest');
        return;
      }

      await ClientService.addClientInterest({
        client_id: selectedClient.id,
        sector_id: selectedSector || undefined,
        company_id: selectedCompany || undefined,
        thematic_query: thematicQuery.trim() || undefined
      });

      const updatedInterests = await ClientService.getClientInterests(selectedClient.id);
      setClientInterests(updatedInterests);
      setSelectedSector(null);
      setSelectedCompany(null);
      setThematicQuery('');
      setSuccess('Interest added successfully!');
    } catch (err) {
      setError('Failed to add interest: ' + (err as Error).message);
    }
  };

  const handleRemoveInterest = async (interest: ClientInterest) => {
    if (!selectedClient) return;

    try {
      await ClientService.removeClientInterest({
        client_id: selectedClient.id,
        sector_id: interest.sector_id || undefined,
        company_id: interest.company_id || undefined,
        thematic_query: interest.thematic_query || undefined
      });

      const updatedInterests = await ClientService.getClientInterests(selectedClient.id);
      setClientInterests(updatedInterests);
      setSuccess('Interest removed successfully!');
    } catch (err) {
      setError('Failed to remove interest: ' + (err as Error).message);
    }
  };

  const handleEmailSent = () => {
    setSuccess('Email sent successfully!');
  };

  const handleAgentToggle = async (enabled: boolean) => {
    if (!selectedClient) return;

    try {
      const updatedClient = await ClientService.updateClient(selectedClient.id, {
        agent_enabled: enabled
      });
      setClients(clients.map(c => c.id === selectedClient.id ? updatedClient : c));
      setSelectedClient(updatedClient);
      setSuccess(`AI Agent ${enabled ? 'enabled' : 'disabled'} for this client`);
    } catch (err) {
      setError('Failed to toggle AI Agent: ' + (err as Error).message);
    }
  };

  const handleNotifyAllToggle = async (enabled: boolean) => {
    if (!selectedClient) return;

    try {
      const updatedClient = await ClientService.updateClient(selectedClient.id, {
        notify_all_documents: enabled
      });
      setClients(clients.map(c => c.id === selectedClient.id ? updatedClient : c));
      setSelectedClient(updatedClient);
      setSuccess(`All-documents notifications ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setError('Failed to toggle all-documents: ' + (err as Error).message);
    }
  };

  const getInterestName = (interest: ClientInterest) => {
    if (interest.sector_id) {
      const sector = sectors.find(s => s.id === interest.sector_id);
      return sector?.name || interest.sector_name || 'Unknown Sector';
    }
    if (interest.company_id) {
      const company = companies.find(c => c.id === interest.company_id);
      return company?.name || 'Unknown Company';
    }
    if (interest.thematic_query) {
      return `Theme: ${interest.thematic_query}`;
    }
    return 'Unknown Interest';
  };

  const getInterestType = (interest: ClientInterest) => {
    if (interest.sector_id) return 'Sector';
    if (interest.company_id) return 'Company';
    if (interest.thematic_query) return 'AI Theme';
    return 'Unknown';
  };

  const filteredClients = clients.filter(client =>
    client.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getWorkplaceName = (workplaceId?: number) => {
    if (!workplaceId) return 'Not assigned';
    const workplace = workplaces.find(w => w.id === workplaceId);
    return workplace?.name || 'Unknown Workplace';
  };

  const getPsagotTeamMemberName = (teamId?: number) => {
    if (!teamId) return 'Not assigned';
    const member = psagotTeam.find(t => t.id === teamId);
    return member ? `${member.first_name} ${member.last_name}` : 'Unknown Member';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1">
          Client Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddClientDialog(true)}
        >
          + ADD CLIENT
        </Button>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}
      >
        <Tab icon={<DashboardIcon />} label="Dashboard" iconPosition="start" />
        <Tab icon={<PeopleIcon />} label="Clients" iconPosition="start" />
        <Tab icon={<BulkIcon />} label="Bulk Actions" iconPosition="start" />
      </Tabs>

      {/* Tab 0: Dashboard */}
      <TabPanel value={activeTab} index={0}>
        <CRMDashboard />
      </TabPanel>

      {/* Tab 1: Clients (enhanced current view) */}
      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          {/* Left Column - Client List */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Client List
                </Typography>

                <TextField
                  fullWidth
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {filteredClients.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No clients found" />
                    </ListItem>
                  ) : (
                    filteredClients.map((client) => (
                      <ListItem
                        key={client.id}
                        button
                        selected={selectedClient?.id === client.id}
                        onClick={() => handleClientSelect(client)}
                      >
                        <ListItemText
                          primary={`${client.first_name || ''} ${client.last_name || ''}`}
                          secondary={client.email}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Client Details */}
          <Grid item xs={12} md={8}>
            {selectedClient ? (
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      {selectedClient.first_name} {selectedClient.last_name}
                    </Typography>
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={() => setShowRelevantDocuments(!showRelevantDocuments)}
                        sx={{ mr: 1 }}
                        size="small"
                      >
                        {showRelevantDocuments ? 'Hide' : 'Show'} Relevant Documents
                      </Button>
                      <IconButton onClick={() => {
                        setEditClient({
                          first_name: selectedClient.first_name,
                          last_name: selectedClient.last_name,
                          email: selectedClient.email,
                          phone_country_code: selectedClient.phone_country_code,
                          phone_number: selectedClient.phone_number,
                          workplace_id: selectedClient.workplace_id,
                          psagot_team_id: selectedClient.psagot_team_id,
                          whatsapp_number: selectedClient.whatsapp_number || '',
                          sms_number: selectedClient.sms_number || '',
                          preferred_language: selectedClient.preferred_language || 'Hebrew',
                          notes: selectedClient.notes || '',
                        });
                        setEditClientDialog(true);
                      }}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Contact Info */}
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Email</Typography>
                      <Typography variant="body1" display="flex" alignItems="center">
                        <EmailIcon sx={{ mr: 1, fontSize: 16 }} />
                        {selectedClient.email}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Phone</Typography>
                      <Typography variant="body1" display="flex" alignItems="center">
                        <PhoneIcon sx={{ mr: 1, fontSize: 16 }} />
                        {selectedClient.phone_country_code} {selectedClient.phone_number || 'Not provided'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">WhatsApp</Typography>
                      <Typography variant="body1" display="flex" alignItems="center">
                        <WhatsAppIcon sx={{ mr: 1, fontSize: 16, color: '#25D366' }} />
                        {selectedClient.whatsapp_number || 'Uses phone number'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">SMS</Typography>
                      <Typography variant="body1" display="flex" alignItems="center">
                        <SmsIcon sx={{ mr: 1, fontSize: 16, color: '#f57c00' }} />
                        {selectedClient.sms_number || 'Uses phone number'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Workplace</Typography>
                      <Typography variant="body1" display="flex" alignItems="center">
                        <WorkIcon sx={{ mr: 1, fontSize: 16 }} />
                        {getWorkplaceName(selectedClient.workplace_id)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">Psagot Team Member</Typography>
                      <Typography variant="body1" display="flex" alignItems="center">
                        <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                        {getPsagotTeamMemberName(selectedClient.psagot_team_id)}
                      </Typography>
                    </Grid>

                    {selectedClient.notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">Notes</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                          {selectedClient.notes}
                        </Typography>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.light', borderRadius: 1, color: 'primary.contrastText' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={selectedClient.agent_enabled || false}
                              onChange={(e) => handleAgentToggle(e.target.checked)}
                              color="secondary"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center">
                              <PsychologyIcon sx={{ mr: 1 }} />
                              <Typography variant="subtitle2">
                                Smart Agent AI Alerts {selectedClient.agent_enabled ? 'ON' : 'OFF'}
                              </Typography>
                            </Box>
                          }
                        />
                        {selectedClient.agent_enabled && (
                          <FormControlLabel
                            sx={{ ml: 3 }}
                            control={
                              <Switch
                                checked={selectedClient.notify_all_documents || false}
                                onChange={(e) => handleNotifyAllToggle(e.target.checked)}
                                color="secondary"
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2">
                                Receive ALL documents (skip interest matching)
                              </Typography>
                            }
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Interests Section */}
                  <Typography variant="h6" gutterBottom>
                    Interests & Notification Settings
                  </Typography>

                  <Box mb={2}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Sector</InputLabel>
                          <Select
                            value={selectedSector || ''}
                            onChange={(e) => setSelectedSector(e.target.value as number)}
                            label="Sector"
                            disabled={!!thematicQuery}
                          >
                            <MenuItem value="">
                              <em>Select sector</em>
                            </MenuItem>
                            {sectors.map((sector) => (
                              <MenuItem key={sector.id} value={sector.id}>
                                {sector.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Company</InputLabel>
                          <Select
                            value={selectedCompany || ''}
                            onChange={(e) => setSelectedCompany(e.target.value as number)}
                            label="Company"
                            disabled={!!thematicQuery}
                          >
                            <MenuItem value="">
                              <em>Select company</em>
                            </MenuItem>
                            {companies.map((company) => (
                              <MenuItem key={company.id} value={company.id}>
                                {company.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="contained"
                          onClick={handleAddInterest}
                          disabled={!selectedSector && !selectedCompany && !thematicQuery.trim()}
                          fullWidth
                        >
                          Add Interest
                        </Button>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="AI Thematic Interest (e.g., 'Fed pivot impact on tech')"
                          value={thematicQuery}
                          onChange={(e) => setThematicQuery(e.target.value)}
                          placeholder="Type a theme for the AI Agent to watch for..."
                          disabled={!!selectedSector || !!selectedCompany}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    {clientInterests.length === 0 ? (
                      <Typography color="textSecondary">No interests added yet</Typography>
                    ) : (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {clientInterests.map((interest) => (
                          <Box key={interest.id} display="flex" alignItems="center">
                            <Chip
                              label={`${getInterestName(interest)} (${getInterestType(interest)})`}
                              onDelete={() => handleRemoveInterest(interest)}
                              color="primary"
                              variant="outlined"
                            />
                            <InterestNotifyControls
                              interest={interest}
                              onUpdated={reloadInterests}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Activity Timeline */}
                  <ActivityTimeline clientId={selectedClient.id} />

                  {/* Relevant Documents Section */}
                  {showRelevantDocuments && (
                    <Box sx={{ mt: 3 }}>
                      <RelevantDocuments
                        client={selectedClient}
                        onEmailSent={handleEmailSent}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <Typography variant="h6" color="textSecondary">
                    Select a client to view details
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 2: Bulk Actions */}
      <TabPanel value={activeTab} index={2}>
        <BulkActions
          clients={clients}
          sectors={sectors}
          companies={companies}
          onSuccess={(msg) => setSuccess(msg)}
          onError={(msg) => setError(msg)}
        />
      </TabPanel>

      {/* Add Client Dialog */}
      <Dialog open={addClientDialog} onClose={() => setAddClientDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Client</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={newClient.first_name}
                onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={newClient.last_name}
                onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newClient.email}
                onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Country Code</InputLabel>
                <Select
                  value={newClient.phone_country_code}
                  onChange={(e) => setNewClient({ ...newClient, phone_country_code: e.target.value })}
                  label="Country Code"
                >
                  {countryCodes.map((country) => (
                    <MenuItem key={country.code} value={country.dial_code}>
                      {country.name} ({country.dial_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Phone Number"
                value={newClient.phone_number}
                onChange={(e) => setNewClient({ ...newClient, phone_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="WhatsApp Number (optional)"
                placeholder="whatsapp:+972..."
                value={newClient.whatsapp_number || ''}
                onChange={(e) => setNewClient({ ...newClient, whatsapp_number: e.target.value })}
                helperText="Leave empty to use phone number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMS Number (optional)"
                placeholder="+972..."
                value={newClient.sms_number || ''}
                onChange={(e) => setNewClient({ ...newClient, sms_number: e.target.value })}
                helperText="Leave empty to use phone number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Workplace</InputLabel>
                <Select
                  value={newClient.workplace_id || ''}
                  onChange={(e) => setNewClient({ ...newClient, workplace_id: e.target.value as number || undefined })}
                  label="Workplace"
                >
                  <MenuItem value="">
                    <em>Select workplace</em>
                  </MenuItem>
                  {workplaces.map((workplace) => (
                    <MenuItem key={workplace.id} value={workplace.id}>
                      {workplace.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Psagot Team Member</InputLabel>
                <Select
                  value={newClient.psagot_team_id || ''}
                  onChange={(e) => setNewClient({ ...newClient, psagot_team_id: e.target.value as number || undefined })}
                  label="Psagot Team Member"
                >
                  <MenuItem value="">
                    <em>Select team member</em>
                  </MenuItem>
                  {psagotTeam.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={newClient.notes || ''}
                onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddClientDialog(false)}>Cancel</Button>
          <Button onClick={handleAddClient} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={editClientDialog} onClose={() => setEditClientDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Client</DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={editClient.first_name ?? selectedClient.first_name ?? ''}
                  onChange={(e) => setEditClient({ ...editClient, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={editClient.last_name ?? selectedClient.last_name ?? ''}
                  onChange={(e) => setEditClient({ ...editClient, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editClient.email ?? selectedClient.email ?? ''}
                  onChange={(e) => setEditClient({ ...editClient, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Country Code</InputLabel>
                  <Select
                    value={editClient.phone_country_code ?? selectedClient.phone_country_code ?? '+972'}
                    onChange={(e) => setEditClient({ ...editClient, phone_country_code: e.target.value })}
                    label="Country Code"
                  >
                    {countryCodes.map((country) => (
                      <MenuItem key={country.code} value={country.dial_code}>
                        {country.name} ({country.dial_code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={editClient.phone_number ?? selectedClient.phone_number ?? ''}
                  onChange={(e) => setEditClient({ ...editClient, phone_number: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="WhatsApp Number"
                  placeholder="whatsapp:+972..."
                  value={editClient.whatsapp_number ?? selectedClient.whatsapp_number ?? ''}
                  onChange={(e) => setEditClient({ ...editClient, whatsapp_number: e.target.value })}
                  helperText="Leave empty to use phone number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SMS Number"
                  placeholder="+972..."
                  value={editClient.sms_number ?? selectedClient.sms_number ?? ''}
                  onChange={(e) => setEditClient({ ...editClient, sms_number: e.target.value })}
                  helperText="Leave empty to use phone number"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Workplace</InputLabel>
                  <Select
                    value={editClient.workplace_id ?? selectedClient.workplace_id ?? ''}
                    onChange={(e) => setEditClient({ ...editClient, workplace_id: e.target.value as number || undefined })}
                    label="Workplace"
                  >
                    <MenuItem value="">
                      <em>Select workplace</em>
                    </MenuItem>
                    {workplaces.map((workplace) => (
                      <MenuItem key={workplace.id} value={workplace.id}>
                        {workplace.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Psagot Team Member</InputLabel>
                  <Select
                    value={editClient.psagot_team_id ?? selectedClient.psagot_team_id ?? ''}
                    onChange={(e) => setEditClient({ ...editClient, psagot_team_id: e.target.value as number || undefined })}
                    label="Psagot Team Member"
                  >
                    <MenuItem value="">
                      <em>Select team member</em>
                    </MenuItem>
                    {psagotTeam.map((member) => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.first_name} {member.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Language</InputLabel>
                  <Select
                    value={editClient.preferred_language ?? selectedClient.preferred_language ?? 'Hebrew'}
                    onChange={(e) => setEditClient({ ...editClient, preferred_language: e.target.value })}
                    label="Preferred Language"
                  >
                    <MenuItem value="Hebrew">Hebrew</MenuItem>
                    <MenuItem value="English">English</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={editClient.notes ?? selectedClient.notes ?? ''}
                  onChange={(e) => setEditClient({ ...editClient, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditClientDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateClient} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Add Workplace Dialog */}
      <Dialog open={addWorkplaceDialog} onClose={() => setAddWorkplaceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Workplace</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Workplace Name"
                value={newWorkplace.name}
                onChange={(e) => setNewWorkplace({ ...newWorkplace, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={newWorkplace.address}
                onChange={(e) => setNewWorkplace({ ...newWorkplace, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={newWorkplace.city}
                onChange={(e) => setNewWorkplace({ ...newWorkplace, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={newWorkplace.country}
                onChange={(e) => setNewWorkplace({ ...newWorkplace, country: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddWorkplaceDialog(false)}>Cancel</Button>
          <Button onClick={handleAddWorkplace} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientManagement;
